import { useState, useRef, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';

export type ActionOption = {
    id: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    condition?: boolean; // If false, option won't be shown
    disabled?: boolean; // If true, option will be grayed out but still visible
    immediate?: boolean; // If true, executes onClick immediately without Apply button
};

interface ActionDropdownProps {
    options: ActionOption[];
    buttonClassName?: string;
    dropdownClassName?: string;
}

export default function ActionDropdown({ 
    options, 
    buttonClassName = '',
    dropdownClassName = ''
}: ActionDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<ActionOption | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter options based on condition
    const availableOptions = options.filter(option => 
        option.condition === undefined || option.condition === true
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSelectedOption(null); // Reset selection when closing
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleApply = () => {
        if (selectedOption && !selectedOption.disabled) {
            selectedOption.onClick();
            setSelectedOption(null);
            setIsOpen(false);
        }
    };

    const handleOptionClick = (option: ActionOption) => {
        if (!option.disabled) {
            if (option.immediate) {
                option.onClick();
                setIsOpen(false);
            } else {
                setSelectedOption(option);
            }
        }
    };

    if (availableOptions.length === 0) {
        return null;
    }

    return (
        <div className="relative inline-block" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
            {/* Dropdown Button */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 hover:text-black dark:hover:bg-gray-700 dark:border-gray-600 ${buttonClassName}`}
                title="Actions"
            >
                <span className="text-sm">Actions</span>
                <FaChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className={`absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-50 min-w-[250px] ${dropdownClassName}`}>
                    <div className="max-h-[500px] overflow-y-auto">
                        {availableOptions.map((option) => (
                            <div
                                key={option.id}
                                onClick={() => handleOptionClick(option)}
                                className={`flex items-center gap-3 px-4 py-2.5 transition-colors text-black dark:text-white ${
                                    option.disabled 
                                        ? 'opacity-50 cursor-not-allowed' 
                                        : 'cursor-pointer hover:bg-orange-1000 hover:text-white'
                                } ${
                                    selectedOption?.id === option.id && !option.disabled ? 'bg-orange-1000 text-white' : ''
                                }`}
                            >
                                <div className="flex items-center justify-center w-5">
                                    {option.icon}
                                </div>
                                <span className="text-sm flex-1">{option.label}</span>
                                {selectedOption?.id === option.id && !option.disabled && (
                                    <div className="w-2 h-2 bg-orange-1000 rounded-full"></div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {/* Apply Button */}
                    {selectedOption && (
                        <div className="border-t border-gray-300 dark:border-gray-600 p-2">
                            <button
                                type="button"
                                onClick={handleApply}
                                className="w-full px-4 py-2 bg-gray-800 hover:bg-orange-1000 text-white rounded text-sm font-medium transition-colors"
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
